import { useState } from "react";
import { toast } from "sonner";
import {
  Share2, Plus, ArrowUpRight, ArrowDownLeft, Eye, FileText, ShieldCheck,
  Send, Handshake, Clock,
} from "lucide-react";
import {
  Button, Card, CardBody, Badge, PageHeader, EmptyState, Loading, AccessDenied,
  Alert, Field, TextInput, Select, Modal, ConfirmDialog, Tabs,
} from "@/components/redvolt/kit";
import {
  GRANT_STATUS, LEVEL_LABEL, orgLabel, fmtDate, useLoader, apiError,
} from "@/app/federation/common";

const GStatus = ({ status }) => {
  const s = GRANT_STATUS[status] || { label: status, variant: "neutral" };
  return <Badge variant={s.variant}>{s.label}</Badge>;
};

/* --------------------------- Create grant ------------------------------- */
const CreateGrantModal = ({ api, tenantId, relationships, categories, onClose, onDone }) => {
  const active = relationships.filter((r) => r.status === "active");
  const [rel, setRel] = useState(active[0]?.id || "");
  const [cat, setCat] = useState(categories[0]?.key || "");
  const catObj = categories.find((c) => c.key === cat);
  const [level, setLevel] = useState(catObj?.levels?.[0] || "read_only");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const onCat = (k) => { setCat(k); const c = categories.find((x) => x.key === k); setLevel(c?.levels?.[0] || "read_only"); };
  const submit = async () => {
    if (!rel || !cat) return;
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/sharing/grants`, {
        relationship_id: rel, category_key: cat, access_level: level,
        expires_at: expires ? new Date(expires).toISOString() : null,
      });
      toast.success("Sharing offer created — approve it to activate");
      onDone(); onClose();
    } catch (e) { apiError(e, "Could not create sharing"); }
    finally { setBusy(false); }
  };
  if (active.length === 0) {
    return (
      <Modal open onClose={onClose} testid="create-grant-modal" title="Share with an organization"
        footer={<Button variant="outline" size="sm" onClick={onClose}>Close</Button>}>
        <Alert variant="info">You need an active connection first. Connect with an organization, then choose what to share.</Alert>
      </Modal>
    );
  }
  return (
    <Modal open onClose={onClose} testid="create-grant-modal" title="Share with an organization"
      description="You decide exactly what to share, at what access level."
      footer={<>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy} data-testid="submit-grant">{busy ? "Creating…" : "Create sharing"}</Button>
      </>}>
      <div className="space-y-4">
        <Field label="Connected organization" htmlFor="grant-rel">
          <Select id="grant-rel" data-testid="grant-rel-select" value={rel} onChange={(e) => setRel(e.target.value)}>
            {active.map((r) => <option key={r.id} value={r.id}>{orgLabel(r)}</option>)}
          </Select>
        </Field>
        <Field label="What to share" htmlFor="grant-cat">
          <Select id="grant-cat" data-testid="grant-cat-select" value={cat} onChange={(e) => onCat(e.target.value)}>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.display}</option>)}
          </Select>
        </Field>
        <Field label="Access level" htmlFor="grant-level">
          <Select id="grant-level" data-testid="grant-level-select" value={level} onChange={(e) => setLevel(e.target.value)}>
            {(catObj?.levels || ["read_only"]).map((l) => <option key={l} value={l}>{LEVEL_LABEL[l] || l}</option>)}
          </Select>
        </Field>
        <Field label="Expires (optional)" htmlFor="grant-exp" hint="Leave blank for no expiry.">
          <TextInput id="grant-exp" type="date" data-testid="grant-exp-input" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
};

/* --------------------------- Shared viewer ------------------------------ */
const SharedViewer = ({ api, tenantId, grant, onClose }) => {
  const q = useLoader(async () => {
    const [res, prov] = await Promise.all([
      api.get(`/tenants/${tenantId}/federation/shared/${grant.id}`),
      api.get(`/tenants/${tenantId}/federation/shared/${grant.id}/provenance`),
    ]);
    return { resources: res.data.resources, prov: prov.data };
  }, [grant.id]);
  const prov = q.data?.prov;
  return (
    <Modal open onClose={onClose} testid="shared-viewer-modal" size="lg"
      title={`Shared: ${grant.category}`}
      footer={<Button variant="outline" size="sm" onClick={onClose}>Close</Button>}>
      {q.loading ? <Loading label="Opening shared information…" /> : q.denied ? (
        <AccessDenied message="This shared access is no longer available." />
      ) : (
        <div className="space-y-4">
          {prov && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="provenance-panel">
              <p className="flex items-center gap-2 font-semibold text-slate-800"><ShieldCheck className="h-4 w-4 text-primary" /> Why you can see this</p>
              <div className="mt-2 grid gap-1 text-slate-600">
                {prov.owner_organization && <p><span className="text-slate-400">Owned by:</span> <span className="font-medium text-slate-800">{prov.owner_organization}</span></p>}
                {prov.receiving_organization && <p><span className="text-slate-400">Shared with:</span> <span className="font-medium text-slate-800">{prov.receiving_organization}</span></p>}
                <p><span className="text-slate-400">Through:</span> <span className="font-medium text-slate-800">{prov.through}</span></p>
                <p><span className="text-slate-400">Access:</span> {LEVEL_LABEL[prov.access_level] || prov.access_level}{prov.expires_at ? ` · until ${fmtDate(prov.expires_at)}` : ""}</p>
              </div>
            </div>
          )}
          <div className="overflow-hidden rounded-lg border border-slate-200" data-testid="shared-resources">
            {(q.data?.resources || []).length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">Nothing shared under this at the moment.</div>
            ) : (q.data.resources.map((r) => (
              <div key={r.id} className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0">
                <span className="grid h-8 w-8 place-items-center rounded-md bg-accent text-primary"><FileText className="h-4 w-4" /></span>
                <span className="text-sm font-medium text-slate-800">{r.title}</span>
              </div>
            )))}
          </div>
        </div>
      )}
    </Modal>
  );
};

/* ------------------------------ Grant card ------------------------------ */
const GrantCard = ({ grant, caps, onApprove, onAck, onRevoke, onView }) => {
  const owner = grant.role === "owner";
  const canManage = caps.includes("sharing:manage");
  const sensitive = grant.policy_class !== "standard";
  const canApprove = owner && grant.needs_approval && (sensitive ? caps.includes("sharing:approve") : canManage);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={`grant-${grant.id}`}>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-900">{grant.category}</p>
        <p className="text-xs text-slate-500">
          {owner ? "To " : "From "}<span className="font-medium">{grant.counterpart_name || grant.context_name || "Partner organization"}</span>
          {" · "}{LEVEL_LABEL[grant.access_level] || grant.access_level}
          {grant.basis === "coordination" && grant.context_name ? ` · via ${grant.context_name}` : ""}
          {grant.expires_at ? ` · until ${fmtDate(grant.expires_at)}` : ""}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <GStatus status={grant.status} />
        {canApprove && <Button size="sm" onClick={() => onApprove(grant)} data-testid={`approve-grant-${grant.id}`}>Approve</Button>}
        {!owner && grant.awaiting_ack && canManage && <Button size="sm" onClick={() => onAck(grant)} data-testid={`ack-grant-${grant.id}`}>Accept</Button>}
        {!owner && grant.status === "active" && caps.includes("federation:access_shared") && (
          <Button size="sm" variant="outline" onClick={() => onView(grant)} data-testid={`view-shared-${grant.id}`}><Eye className="h-4 w-4" /> View</Button>
        )}
        {owner && ["active", "offered"].includes(grant.status) && canManage && (
          <Button size="sm" variant="destructive" onClick={() => onRevoke(grant)} data-testid={`revoke-grant-${grant.id}`}>Revoke</Button>
        )}
      </div>
    </div>
  );
};

/* ------------------------------- Page ----------------------------------- */
export default function SharingPage({ api, active, caps }) {
  const tenantId = active.tenant_id;
  const [tab, setTab] = useState("outgoing");
  const [showCreate, setShowCreate] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [revoke, setRevoke] = useState(null);
  const [busy, setBusy] = useState(false);

  const canView = caps.includes("sharing:view");
  const canManage = caps.includes("sharing:manage");

  const grantsQ = useLoader(async () => (await api.get(`/tenants/${tenantId}/sharing/grants`)).data.grants, [tenantId]);
  const relsQ = useLoader(async () => (await api.get(`/tenants/${tenantId}/relationships`)).data.relationships, [tenantId]);
  const catsQ = useLoader(async () => (await api.get(`/reference/sharing-categories`)).data.categories, []);
  const grants = grantsQ.data || [];

  const act = async (grant, verb, ok) => {
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/sharing/grants/${grant.id}/${verb}`);
      toast.success(ok);
      grantsQ.reload();
    } catch (e) { apiError(e); }
    finally { setBusy(false); setRevoke(null); }
  };

  if (!canView) return <AccessDenied message="Your role doesn’t include viewing sharing." />;

  const outgoing = grants.filter((g) => g.role === "owner");
  const incoming = grants.filter((g) => g.role === "accessor");
  const tabs = [
    { key: "outgoing", label: "We share with them", count: outgoing.length },
    { key: "incoming", label: "They share with us", count: incoming.length },
  ];

  return (
    <div className="space-y-6" data-testid="sharing-page">
      <PageHeader title="Manage Sharing"
        subtitle="Sharing is explicit and directional. Nothing is shared unless you set it up here."
        action={canManage && <Button onClick={() => setShowCreate(true)} data-testid="create-grant-button"><Plus className="h-4 w-4" /> Share something</Button>} />

      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {grantsQ.loading ? <Loading label="Loading sharing…" /> : (
        <>
          {tab === "outgoing" && (
            outgoing.length === 0 ? (
              <EmptyState icon={ArrowUpRight} title="You’re not sharing anything yet"
                description="Connections don’t expose any data. Choose exactly what to share with a connected organization, and at what access level."
                action={canManage && <Button size="sm" onClick={() => setShowCreate(true)}>Share something</Button>} />
            ) : (
              <div className="space-y-2">
                {outgoing.map((g) => <GrantCard key={g.id} grant={g} caps={caps}
                  onApprove={(x) => act(x, "approve", "Sharing approved")}
                  onRevoke={(x) => setRevoke(x)} />)}
              </div>
            )
          )}
          {tab === "incoming" && (
            incoming.length === 0 ? (
              <EmptyState icon={ArrowDownLeft} title="Nothing shared with you yet"
                description="When another organization shares something with you, it will appear here with a clear explanation of why you can see it." />
            ) : (
              <div className="space-y-2">
                {incoming.map((g) => <GrantCard key={g.id} grant={g} caps={caps}
                  onAck={(x) => act(x, "acknowledge", "Accepted")}
                  onView={(x) => setViewer(x)} />)}
              </div>
            )
          )}
        </>
      )}

      {showCreate && <CreateGrantModal api={api} tenantId={tenantId} relationships={relsQ.data || []} categories={catsQ.data || []} onClose={() => setShowCreate(false)} onDone={grantsQ.reload} />}
      {viewer && <SharedViewer api={api} tenantId={tenantId} grant={viewer} onClose={() => setViewer(null)} />}
      <ConfirmDialog open={!!revoke} onCancel={() => setRevoke(null)} onConfirm={() => act(revoke, "revoke", "Sharing revoked")} busy={busy}
        title="Revoke this sharing?" confirmLabel="Revoke">
        The receiving organization will immediately lose access to this. Existing activity history is kept.
      </ConfirmDialog>
    </div>
  );
}
